import { brandFor, type Brand } from './brands.ts';
import { CategoryIcon } from './primitives.tsx';

export { brandFor, type Brand } from './brands.ts';

/** Relative luminance of a six-digit hex colour. */
function luminance(hex: string): number {
  const [r, g, b] = [0, 2, 4].map((i) => {
    const c = parseInt(hex.slice(i, i + 2), 16) / 255;
    return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  }) as [number, number, number];
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

export function BrandLogo({ brand, size = 34, round = false }: { brand: Brand; size?: number; round?: boolean }) {
  const radius = round ? '50%' : undefined;
  if (brand.image) {
    // The tile as stored: its own colours on its own ground, in both themes (as an app icon would be).
    return (
      <span className="brand-logo brand-logo--image" style={{ width: size, height: size, borderRadius: radius }} title={brand.name} aria-hidden="true">
        <img src={brand.image} alt="" width={size} height={size} draggable={false} />
      </span>
    );
  }
  // The mark or the initial in white on the brand's colour; on a light colour, in ink.
  const light = luminance(brand.hex) > 0.4;
  return (
    <span className="brand-logo" style={{ width: size, height: size, borderRadius: radius, background: `#${brand.hex}`, color: light ? '#1d1b16' : '#ffffff' }} title={brand.name} aria-hidden="true">
      {brand.path ? (
        <svg viewBox="0 0 24 24" width={Math.round(size * 0.58)} height={Math.round(size * 0.58)}>
          <path d={brand.path} fill="currentColor" />
        </svg>
      ) : (
        <span className="brand-logo__initial" style={{ fontSize: brand.initial && brand.initial.length > 1 ? size * 0.36 : size * 0.5 }}>
          {brand.initial}
        </span>
      )}
    </span>
  );
}

/** The merchant's brand logo when it is a known one, otherwise the category icon. */
export function MerchantIcon({ texts, icon, color }: { texts: readonly (string | null | undefined)[]; icon: string | null; color: string | null }) {
  const brand = brandFor(...texts);
  return brand ? <BrandLogo brand={brand} /> : <CategoryIcon icon={icon} color={color} />;
}
